/**
 * Discover-grade image crops for the Article JSON-LD `image` array (and, for /discover guides,
 * `og:image` too — see `discoverGuideHead` in `../marketingRoutes.tsx`).
 *
 * Google Discover has a stated 1200px-wide minimum and recommends offering more than one aspect
 * ratio per article. Measured on production 2026-09-26: the /discover guides' Unsplash image and
 * the blog's Pexels image both carry only one ratio, and the guides' sits at `w=1080` — under the
 * minimum. This builds three crops — 16:9, 4:3, 1:1 — all >= 1200px wide, for the two CDNs the
 * site's hero images actually come from.
 *
 * Anything else (our own `/og-share-image.jpg`, Strapi/R2 media URLs) passes through unchanged:
 * we don't control those hosts' resizing API, so there is nothing to build a crop query against.
 *
 * Order is fixed — 16:9, 4:3, 1:1 — so a caller that wants "the" single image (an `og:image`, an
 * RSS `media:content`) can take index 0 without re-deriving which one that is.
 */

interface CropSpec {
	width: number;
	height: number;
}

// 16:9, 4:3, 1:1 — in this order because callers that need only one image take index 0.
const DISCOVER_CROPS: CropSpec[] = [
	{ width: 1600, height: 900 },
	{ width: 1600, height: 1200 },
	{ width: 1200, height: 1200 },
];

/**
 * `images.unsplash.com`'s imgix-style query API. `fit=max` and `dpr` both fight an explicit crop
 * size — `fit=max` lets the source stay under the bound instead of filling it, and `dpr`
 * multiplies the already-final `w` again — so both are dropped rather than kept alongside a new
 * `w`/`h`. `ixid`/`ixlib` (the asset identity Unsplash's API keys off) and any existing `cs` are
 * left untouched: nothing about a crop conflicts with them.
 */
function unsplashCrop(base: URL, crop: CropSpec): string {
	const u = new URL(base.toString());
	const params = u.searchParams;
	params.delete("dpr");
	params.set("w", String(crop.width));
	params.set("h", String(crop.height));
	params.set("fit", "crop");
	params.set("crop", "entropy");
	if (!params.has("q")) params.set("q", "80");
	params.set("fm", "jpg");
	u.search = params.toString();
	return u.toString();
}

/** `images.pexels.com`'s query API. `dpr=2` (the blog's shape) doubles `w` again on top of an explicit crop size, so it's replaced with `dpr=1` rather than left to compound. */
function pexelsCrop(base: URL, crop: CropSpec): string {
	const u = new URL(base.toString());
	const params = u.searchParams;
	params.set("w", String(crop.width));
	params.set("h", String(crop.height));
	params.set("fit", "crop");
	params.set("auto", "compress");
	params.set("cs", "tinysrgb");
	params.set("dpr", "1");
	u.search = params.toString();
	return u.toString();
}

/**
 * Three Discover-grade crop variants (16:9, 4:3, 1:1) for a known-CDN hero image, or `[url]`
 * unchanged for anything else — including an unparsable string, so a relative path (our own
 * `/og-share-image.jpg` before a `baseUrl` prefix is applied) degrades to a no-op instead of
 * throwing.
 */
export function discoverImageVariants(url: string): string[] {
	if (!url) return [];

	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return [url];
	}

	if (parsed.hostname === "images.unsplash.com") {
		return DISCOVER_CROPS.map((crop) => unsplashCrop(parsed, crop));
	}
	if (parsed.hostname === "images.pexels.com") {
		return DISCOVER_CROPS.map((crop) => pexelsCrop(parsed, crop));
	}
	return [url];
}
